/**
 * ConnectionSuggestions Component
 *
 * Displays "People You May Know" suggestions based on:
 * - Mutual organization members
 * - Project collaborators
 * - Friends of friends (mutual connections)
 *
 * Includes dismiss functionality to hide suggestions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserPlus,
  X,
  Building2,
  FolderGit2,
  Link2,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { connectionsApi } from '@/services/api/connections';
import { useConnectionActions } from '@/stores/connection/connection-store';
import { toast } from 'sonner';
import type { ConnectionSuggestion } from '@/types/connections';

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a user
 */
const getDisplayName = (suggestion: ConnectionSuggestion): string => {
  if (suggestion.firstName && suggestion.lastName) {
    return `${suggestion.firstName} ${suggestion.lastName}`;
  }
  if (suggestion.firstName) {
    return suggestion.firstName;
  }
  return suggestion.username;
};

/**
 * Get icon for suggestion reason
 */
const getReasonIcon = (reason: ConnectionSuggestion['reason']) => {
  switch (reason) {
    case 'mutual_organization':
      return <Building2 className="h-3 w-3" />;
    case 'project_collaborator':
      return <FolderGit2 className="h-3 w-3" />;
    case 'mutual_connection':
      return <Link2 className="h-3 w-3" />;
    default:
      return <Users className="h-3 w-3" />;
  }
};

/**
 * Individual suggestion card - Compact version (for dashboard)
 */
interface SuggestionCardProps {
  suggestion: ConnectionSuggestion;
  onConnect: (userId: string) => Promise<void>;
  onDismiss: (userId: string) => Promise<void>;
  onNavigate: (username: string) => void;
  isConnecting: boolean;
  isDismissing: boolean;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  onConnect,
  onDismiss,
  onNavigate,
  isConnecting,
  isDismissing,
}) => {
  const displayName = getDisplayName(suggestion);
  const initials = getInitials(suggestion.firstName, suggestion.lastName, suggestion.username);

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
      {/* Avatar - clickable */}
      <button
        onClick={() => onNavigate(suggestion.username)}
        className="flex-shrink-0"
      >
        <Avatar className="h-10 w-10 ring-2 ring-transparent hover:ring-primary/20 transition-all">
          <AvatarImage src={suggestion.avatar} alt={displayName} />
          <AvatarFallback className="text-sm font-medium bg-primary/10 text-primary">
            {initials}
          </AvatarFallback>
        </Avatar>
      </button>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <button
          onClick={() => onNavigate(suggestion.username)}
          className="text-left"
        >
          <p className="text-sm font-medium truncate hover:text-primary transition-colors">
            {displayName}
          </p>
          <p className="text-xs text-muted-foreground">@{suggestion.username}</p>
        </button>

        {/* Reason badge */}
        <div className="flex items-center gap-1 mt-1">
          <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5">
            {getReasonIcon(suggestion.reason)}
            <span className="ml-1">{suggestion.reasonDetails || suggestion.reason}</span>
          </Badge>
        </div>

        {/* Mutual connections count */}
        {suggestion.mutualConnectionsCount > 0 && suggestion.reason !== 'mutual_connection' && (
          <p className="text-xs text-muted-foreground mt-1">
            {suggestion.mutualConnectionsCount} mutual connection{suggestion.mutualConnectionsCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                className="h-7 px-2 bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => onConnect(suggestion.id)}
                disabled={isConnecting}
              >
                {isConnecting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <UserPlus className="h-3 w-3" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Connect</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={() => onDismiss(suggestion.id)}
                disabled={isDismissing}
              >
                {isDismissing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <X className="h-3 w-3" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Dismiss</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

/**
 * Profile-style suggestion card - Expanded version (for full page)
 */
const SuggestionProfileCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  onConnect,
  onDismiss,
  onNavigate,
  isConnecting,
  isDismissing,
}) => {
  const displayName = getDisplayName(suggestion);
  const initials = getInitials(suggestion.firstName, suggestion.lastName, suggestion.username);

  return (
    <div className="relative flex flex-col items-center p-4 rounded-lg border bg-card shadow-sm hover:shadow-md transition-all duration-200 group">
      {/* Dismiss button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => onDismiss(suggestion.id)}
              disabled={isDismissing}
            >
              {isDismissing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <X className="h-3 w-3 text-muted-foreground" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Dismiss</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Avatar */}
      <button
        onClick={() => onNavigate(suggestion.username)}
        className="mb-2"
      >
        <Avatar className="h-14 w-14 ring-2 ring-background shadow hover:scale-105 transition-transform">
          <AvatarImage src={suggestion.avatar} alt={displayName} />
          <AvatarFallback className="text-lg font-semibold bg-primary/10 text-primary">
            {initials}
          </AvatarFallback>
        </Avatar>
      </button>

      {/* Name and username */}
      <button
        onClick={() => onNavigate(suggestion.username)}
        className="text-center mb-1"
      >
        <h3 className="font-medium text-sm hover:text-primary transition-colors line-clamp-1">
          {displayName}
        </h3>
        <p className="text-xs text-muted-foreground">@{suggestion.username}</p>
      </button>

      {/* Reason badge */}
      <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 mb-2">
        {getReasonIcon(suggestion.reason)}
        <span className="ml-1">{suggestion.reasonDetails || suggestion.reason}</span>
      </Badge>

      {/* Mutual connections */}
      {suggestion.mutualConnectionsCount > 0 && suggestion.reason !== 'mutual_connection' && (
        <p className="text-[10px] text-muted-foreground mb-2">
          {suggestion.mutualConnectionsCount} mutual
        </p>
      )}

      {/* Connect button */}
      <Button
        size="sm"
        className="w-full mt-auto h-8 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
        onClick={() => onConnect(suggestion.id)}
        disabled={isConnecting}
      >
        {isConnecting ? (
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
        ) : (
          <UserPlus className="h-3 w-3 mr-1" />
        )}
        Connect
      </Button>
    </div>
  );
};

/**
 * Loading skeleton for suggestions
 */
const SuggestionsSkeleton: React.FC = () => (
  <div className="space-y-2">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-start gap-3 p-3 rounded-lg border">
        <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
        <div className="flex-1">
          <Skeleton className="h-4 w-32 mb-1" />
          <Skeleton className="h-3 w-24 mb-2" />
          <Skeleton className="h-5 w-40" />
        </div>
        <div className="flex flex-col gap-1">
          <Skeleton className="h-7 w-8" />
          <Skeleton className="h-7 w-8" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * ConnectionSuggestions - Main component
 */
interface ConnectionSuggestionsProps {
  /** Maximum number of suggestions to display */
  limit?: number;
  /** Show as a compact card (for dashboard) or expanded view */
  compact?: boolean;
  /** Callback when a suggestion is connected */
  onConnect?: () => void;
}

export const ConnectionSuggestions: React.FC<ConnectionSuggestionsProps> = ({
  limit = 5,
  compact = true,
  onConnect: onConnectCallback,
}) => {
  const navigate = useNavigate();
  const { sendRequest } = useConnectionActions();

  const [suggestions, setSuggestions] = useState<ConnectionSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);

  const fetchSuggestions = useCallback(async () => {
    console.log('[ConnectionSuggestions] Fetching suggestions, limit:', limit);
    setIsLoading(true);
    setError(null);

    try {
      const response = await connectionsApi.getSuggestions({ limit });

      if (response.success && response.data) {
        setSuggestions(response.data.suggestions);
        console.log('[ConnectionSuggestions] Loaded:', response.data.suggestions.length, 'suggestions');
      } else {
        setError('Failed to load suggestions');
      }
    } catch (err) {
      console.error('[ConnectionSuggestions] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load suggestions');
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const handleConnect = async (userId: string) => {
    console.log('[ConnectionSuggestions] Connecting to user:', userId);
    setConnectingId(userId);

    try {
      await sendRequest(userId);
      // Remove from suggestions list
      setSuggestions((prev) => prev.filter((s) => s.id !== userId));
      onConnectCallback?.();
    } catch (err) {
      console.error('[ConnectionSuggestions] Connect error:', err);
      // Error toast is shown by sendRequest
    } finally {
      setConnectingId(null);
    }
  };

  const handleDismiss = async (userId: string) => {
    console.log('[ConnectionSuggestions] Dismissing suggestion:', userId);
    setDismissingId(userId);

    try {
      await connectionsApi.dismissSuggestion(userId);
      // Remove from suggestions list
      setSuggestions((prev) => prev.filter((s) => s.id !== userId));
      toast.success('Suggestion dismissed');
    } catch (err) {
      console.error('[ConnectionSuggestions] Dismiss error:', err);
      toast.error('Failed to dismiss suggestion');
    } finally {
      setDismissingId(null);
    }
  };

  const handleNavigate = (username: string) => {
    console.log('[ConnectionSuggestions] Navigating to user:', username);
    navigate(`/user/${username}`);
  };

  // Don't render if no suggestions and not loading
  if (!isLoading && suggestions.length === 0 && !error) {
    return null;
  }

  if (compact) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">People You May Know</CardTitle>
            </div>
            {!isLoading && suggestions.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchSuggestions}
                className="h-7 px-2"
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
            )}
          </div>
          <CardDescription>
            Connect with people from your network
          </CardDescription>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <SuggestionsSkeleton />
          ) : error ? (
            <div className="text-center py-4">
              <p className="text-sm text-destructive mb-2">{error}</p>
              <Button variant="outline" size="sm" onClick={fetchSuggestions}>
                Try Again
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {suggestions.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  onConnect={handleConnect}
                  onDismiss={handleDismiss}
                  onNavigate={handleNavigate}
                  isConnecting={connectingId === suggestion.id}
                  isDismissing={dismissingId === suggestion.id}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Expanded view for full page
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            People You May Know
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Connect with people from your organizations and projects
          </p>
        </div>
        {!isLoading && suggestions.length > 0 && (
          <Button variant="outline" onClick={fetchSuggestions}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex flex-col items-center p-4 rounded-lg border bg-card">
              <Skeleton className="h-14 w-14 rounded-full mb-2" />
              <Skeleton className="h-4 w-24 mb-1" />
              <Skeleton className="h-3 w-16 mb-2" />
              <Skeleton className="h-5 w-28 mb-2" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12 px-4">
          <div className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-primary/10">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={fetchSuggestions}>Try Again</Button>
        </div>
      ) : suggestions.length === 0 ? (
        <div className="text-center py-12 px-4">
          <div className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-primary/10">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-lg font-medium mb-2">No suggestions yet</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            As you join organizations and collaborate on projects, we'll suggest people you may want to connect with.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {suggestions.map((suggestion) => (
            <SuggestionProfileCard
              key={suggestion.id}
              suggestion={suggestion}
              onConnect={handleConnect}
              onDismiss={handleDismiss}
              onNavigate={handleNavigate}
              isConnecting={connectingId === suggestion.id}
              isDismissing={dismissingId === suggestion.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ConnectionSuggestions;
